/**
 * Conjectures on screen, where the reader's word for one is "insight" (D333):
 * the record a conjecture node carries, and the presses that propose them,
 * search for them and test them. What each has been through, across the graph,
 * is its card in the report's Insights section, which the Overview's fold
 * renders (`core/reportSections.ts`, D288, D291).
 *
 * Every sentence here describes a RECORD — what a pass proposed, when, and what
 * has since happened to it — and none describes the conjecture's worth. There is
 * no score, no rank and no badge, and no copy in this file calls a conjecture
 * new, novel or a discovery, under either word: whether anyone has said it
 * before is a search's to report (see `core/conjecture.ts`), and
 * `ConjectureBlock.test.tsx` scans this file for the words.
 */

import { useId } from 'react'
import type { CSSProperties } from 'react'
import { entryFor, missingKeyNeed, resolveFor, useStore } from '../core/store'
import { PROVIDERS, type ModelEntry } from '../core/models'
import {
  CONJECTURE_LINE_WORD,
  DEPARTS_LOCK_REFUSAL,
  EXECUTABLE_LOCK_REFUSAL,
  upNextPriceVerb,
  upNextRole,
  upNextShut,
  type ConjectureLineVerb,
} from '../core/upNext'
import { PRIOR_ART_CLOSENESS_WORD, STATUS_WORD } from '../core/vocabulary'
import type { PriorArtEntry, ReasonNode, Session } from '../core/types'
import { READING_LABEL } from '../core/types'
import {
  CONJECTURE_MAX_YIELD,
  CONJECTURE_MOVE_LABEL,
  consensusNodeOf,
  predictionSentence,
  registrationLive,
} from '../core/conjecture'
import {
  PRIOR_ART_MAX_SEARCHES,
  laterStatements,
  latestPriorArt,
  measuredSentence,
  priorArtLabel,
  priorArtLive,
  priorArtNote,
  searchedRegistration,
} from '../core/priorArt'
import { hostOf } from '../core/url'
import { SWEEP_KIND_LABEL, oneSiteAnchors, sweepItemById } from '../core/sweep'
import {
  EXECUTABLE_METHOD_LABEL,
  EXECUTED_NOT_A_RESULT,
  conjectureContracts,
  executableRefusal,
  executedSentence,
  rangesSentence,
} from '../core/executableConjecture'
import {
  EXECUTABLE_NEED,
  PRIOR_ART_NEED,
  SWEEP_NEED,
  landDepartsFrom,
  runExecutableTest,
} from '../engine/reason'
import { projectVerbUsd } from '../engine/run'
import { calendarDay } from '../core/dates'
import { Icon } from './components/Icon'
import { VerbPrice } from './VerbPrice'
import { missingKeyLine, pressName, pressTail } from './pressName'
import { pressVerb } from './upNextPress'
import { useVoiceOn } from './useVoiceOn'
import { useFigureBasis } from './useFigureBasis'
import './Conjectures.css'

/** What an absent search means, said once and read everywhere. */
export const UNSEARCHED_NOTE =
  'Whether anyone has already said this has not been searched. The proposing model was told to leave out anything it recognised, and it cannot know what it never read.'

/** The record a conjecture node carries, in the inspector. */
export function ConjectureRecordBlock({ session, node }: { session: Session; node: ReasonNode }) {
  const select = useStore((s) => s.select)
  const revealNode = useStore((s) => s.revealNode)
  const c = node.conjecture
  if (!c) return null
  const live = registrationLive(node)
  const rewrites = c.registrations?.length ?? 0
  const oneSite = oneSiteAnchors(session, c.anchors)
  return (
    <div className="group cj-record" style={{ gap: 'var(--s2)' }}>
      <span className="eyebrow">Insight · {CONJECTURE_MOVE_LABEL[c.move]}</span>
      <dl className="cj-fields">
        <dt>Departs from</dt>
        <dd>{c.departsFrom}</dd>
        <dt>The leap</dt>
        <dd>{c.leap}</dd>
        <dt>Predicts</dt>
        <dd>{predictionSentence(node)}</dd>
        <dt>Killed by</dt>
        <dd>{c.killTest}</dd>
      </dl>

      {c.anchors.length > 0 && (
        <p className="insp-deeper">
          <span className="eyebrow">Offered as explaining</span>
          {c.anchors.map((id, i) => {
            const target = session.nodes[id]
            // A sweep item is what one page said, shown as one: its host
            // beside its words, never a node's label.
            const item = target ? undefined : sweepItemById(session, id)?.item
            return (
              <span key={id}>
                {i > 0 && <span aria-hidden> · </span>}
                {target ? (
                  <button
                    onClick={() => {
                      revealNode(id)
                      select(id)
                    }}
                  >
                    {target.label}
                  </button>
                ) : item ? (
                  <span>
                    {SWEEP_KIND_LABEL[item.kind]} on {hostOf(item.url)}: {item.statement}
                  </span>
                ) : (
                  <span>a record since removed</span>
                )}
              </span>
            )
          })}
        </p>
      )}
      {oneSite && (
        <p className="gnote gnote--warn">
          Everything this insight offers to explain comes from one site, {oneSite}: one site's
          account of what is unsettled, however many of its pages it spans.
        </p>
      )}

      <p className="gnote gnote--quiet">
        Registered {calendarDay(c.registered.at)}
        {rewrites > 0
          ? `, after ${rewrites} earlier registration${rewrites === 1 ? '' : 's'} that a rewrite replaced`
          : ''}
        . The anchors are the model's own pointers, shown as offered — nothing checks that the
        insight explains them.
      </p>
      {!live && (
        <p className="gnote gnote--warn">
          The wording on screen is not the wording registered on {calendarDay(c.registered.at)}. A
          result recorded against it is a result against words that were changed.
        </p>
      )}
      <PriorArtRecord node={node} />
      <ExecutedRecord session={session} node={node} />
    </div>
  )
}

/**
 * The executable tests proposed for a conjecture, and what each receipt read
 * (D283). Derived on every render from Research Lab's workspace: nothing here is
 * stored on the node, and nothing here is its outcome.
 */
export function ExecutedRecord({ session, node }: { session: Session; node: ReasonNode }) {
  const setResearchOpen = useStore((s) => s.setResearchOpen)
  const contracts = conjectureContracts(session, node)
  if (!contracts.length) return null
  return (
    <div className="group cj-exec" style={{ gap: 'var(--s1)' }}>
      <span className="eyebrow">Executable tests</span>
      {contracts.map((c) => (
        <div key={c.spec.id} className="group" style={{ gap: 'var(--s1)' }}>
          <p className="gnote">
            <b>{c.spec.title}</b>
            {c.design
              ? ` — ${EXECUTABLE_METHOD_LABEL[c.design.method]}, written by ${c.design.model}`
              : ''}
            . It measures {c.spec.metric.name} in {c.spec.metric.unit}; {rangesSentence(c)}.
            {c.wording === 'earlier'
              ? ' It was written for an earlier wording of this insight.'
              : ''}
          </p>
          {c.design && <p className="gnote gnote--quiet">{c.design.reach}</p>}
          {c.results.length === 0 ? (
            <p className="gnote gnote--quiet">
              Not run. Research Lab holds its code, its data and the press that runs it in the
              sandboxed local runner. A model wrote its evaluator: read it before you trust what it
              measures.
            </p>
          ) : (
            c.results.map((r) => (
              <p key={r.receiptId} className="gnote" data-reading={r.reading}>
                {executedSentence(r)}
              </p>
            ))
          )}
        </div>
      ))}
      <p className="gnote gnote--quiet">{EXECUTED_NOT_A_RESULT}</p>
      <button className="btn btn--outline" onClick={() => setResearchOpen(true, 'tests')}>
        Open in Research Lab
      </button>
    </div>
  )
}

/** What the quote check could say about one listed page. */
function quoteNote(e: PriorArtEntry): string {
  if (e.quoteFound === true) return 'The quoted sentence is on the page.'
  if (e.quoteFound === false) {
    return 'The quoted sentence is not in the page text this app received, so this page is read as partly stating it at most.'
  }
  return 'The quote could not be checked against the page: no page text came back.'
}

/**
 * The newest prior-art search on a conjecture: its reading, what it listed, what
 * the backstop found close, and the arithmetic that makes an absence auditable.
 * The unsearched note where no search has run.
 */
export function PriorArtRecord({ node }: { node: ReasonNode }) {
  const s = latestPriorArt(node)
  if (!s) return <p className="gnote gnote--quiet">{UNSEARCHED_NOTE}</p>
  const live = priorArtLive(node, s)
  const measured = measuredSentence(node, s)
  const earlier = (node.conjecture?.priorArt?.length ?? 1) - 1
  const later = node.conjecture ? laterStatements(node.conjecture, s) : []
  const registered = node.conjecture ? searchedRegistration(node.conjecture, s) : undefined
  const q = s.queries.length
  return (
    <div className="group cj-prior" style={{ gap: 'var(--s1)' }}>
      <span className="eyebrow">
        Prior-art search · {calendarDay(s.searchedAt)} · {s.model.name}
      </span>
      <p className="cj-reading" data-reading={s.reading}>
        <b>{priorArtLabel(s, later)}.</b> {priorArtNote(s, undefined, later)}
      </p>
      {later.length > 0 && registered && (
        <p className="gnote" data-later>
          Stated elsewhere after this was registered on {calendarDay(registered.at)}:{' '}
          {later.map((e) => `${e.title} (${e.year}, as the page dates itself)`).join('; ')}. A
          record of a later statement, not a verdict on either.
        </p>
      )}
      {!live && (
        <p className="gnote gnote--warn">
          This search was of wording since rewritten. Search again to read the insight as it stands.
        </p>
      )}
      {s.entries.length > 0 && (
        <ul className="cj-art">
          {s.entries.map((e) => (
            <li key={e.url}>
              <a href={e.url} target="_blank" rel="noreferrer">
                {e.title}
              </a>{' '}
              <span className="cj-art-meta">
                {PRIOR_ART_CLOSENESS_WORD[e.closeness]}
                {e.year !== undefined ? ` · ${e.year}` : ''} · {hostOf(e.url)}
              </span>
              {e.covers && <span className="cj-art-covers">{e.covers}</span>}
              {e.quote && <q className="cj-art-quote">{e.quote}</q>}
              <span className="gnote gnote--quiet">{quoteNote(e)}</span>
            </li>
          ))}
        </ul>
      )}
      {s.backstop.hits.length > 0 && (
        <div className="group" style={{ gap: 'var(--s1)' }}>
          <span className="eyebrow">Close by term overlap — read these</span>
          <ul className="cj-art">
            {s.backstop.hits.map((h) => (
              <li key={h.url}>
                <a href={h.url} target="_blank" rel="noreferrer">
                  {h.title}
                </a>{' '}
                <span className="cj-art-meta">
                  {Math.round(h.leap * 100)}% of the leap's terms, {Math.round(h.prediction * 100)}%
                  of the prediction's · {hostOf(h.url)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="gnote gnote--quiet">
        {q} quer{q === 1 ? 'y' : 'ies'} · {s.seen} distinct page{s.seen === 1 ? '' : 's'} returned ·{' '}
        {s.backstop.close} close by term overlap
        {s.dropped
          ? ` · ${s.dropped} named page${s.dropped === 1 ? '' : 's'} the search never returned, deleted unread`
          : ''}
        .
      </p>
      {s.summary && (
        <p className="gnote">
          {s.model.name}'s report of what came back: {s.summary}
        </p>
      )}
      {measured && <p className="gnote">{measured}</p>}
      {s.failures && s.failures.length > 0 && (
        <ul className="bullets">
          {s.failures.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}
      {earlier > 0 && (
        <p className="gnote gnote--quiet">
          {earlier} earlier search{earlier === 1 ? ' is' : 'es are'} kept in the record.
        </p>
      )}
    </div>
  )
}

/* ── the presses ─────────────────────────────────────────────────────── */

/**
 * What a paid press on this line reads of the route its pass runs on, never of
 * the selection (invariant 4, D249): whether it has a key; the model it would go
 * to, for a search that model cannot make; D202's sentence where there is no key,
 * over the same need its pass opens the key card on (`missingKeyLine`), so the
 * note before the press and the card after it name one route; and the estimate,
 * through the arithmetic `VerbPrice` prints. The conjecture pass and the
 * executable test are reasoning passes priced as a move; the prior-art search and
 * the sweep are the check route's, priced by their own allowances (D281).
 */
export function useLineRoute(verb: ConjectureLineVerb | 'executable'): {
  ready: boolean
  entry: ModelEntry
  keyLine: string
  est: number | null
} {
  const role = verb === 'executable' ? 'reason' : upNextRole(verb)
  const ready = useStore((s) => resolveFor(s, role) !== null)
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  // Its figure includes the voice's line while the voice is on (D295).
  useVoiceOn()
  useFigureBasis()
  const view = { modelId, roleModels }
  const need =
    verb === 'priorArt'
      ? PRIOR_ART_NEED
      : verb === 'sweep'
        ? SWEEP_NEED
        : verb === 'executable'
          ? EXECUTABLE_NEED
          : undefined
  const missing = missingKeyNeed(view, role, need)
  return {
    ready,
    entry: entryFor(view, role),
    keyLine: missingKeyLine(PROVIDERS[missing.provider], missing.need),
    est: projectVerbUsd(view, verb === 'executable' ? 'move' : upNextPriceVerb(verb)),
  }
}

/**
 * A paid press's visible words: its name, then — where its pass can run — the
 * estimate it buys, at rest, on the desktop and the phone alike, as the survey's
 * press prints its own. The figure used to ride only in the hover swap
 * (`VerbPrice`), whose reveal belongs to the node card's action rows, and read
 * "$0.00 → $0.40" to the tree while nobody could see it. Where the press will not
 * spend there is no figure here: the cell prints the status word in its place.
 */
export const pressFace = (word: string, runs: boolean, est: number | null): string =>
  pressName(word, runs ? pressTail({ ready: true, est }) : null)

/** What a keyless note says after D202's sentence. */
export const NO_DEMO = 'There is no demo version of it.'

/** What the conjecture press buys, and what it will not claim. */
export const CONJECTURE_PRESS_NOTE = `Up to ${CONJECTURE_MAX_YIELD} claims no source the model knows states, each with a prediction that would tell it apart — not a finding that nobody has said them.`

/** What the prior-art press buys, and the most it can report. */
export const PRIOR_ART_PRESS_NOTE = `Up to ${PRIOR_ART_MAX_SEARCHES} searches for where this has already been said; the most it can report is that it did not find it.`

/**
 * The prior-art press. Paid, on the check role, and it says before it is pressed
 * that the most it can report is an absence. It waits on the Up next line as the
 * source check does (D286).
 */
export function PriorArtPress({ node }: { node: ReasonNode }) {
  const route = useLineRoute('priorArt')
  const shut = useStore((s) => upNextShut(s, 'priorArt'))
  // V on a conjecture is this search (D288), and like U it acts on the
  // selection, so its chip is promised only on the selected node's card.
  const keysLive = useStore((s) => s.selectedId === node.id)
  const noteId = useId()
  if (!node.conjecture) return null
  // A route with no search still presses, and its pass says why it refuses; the
  // note says so first, and nothing prices a search that cannot happen.
  const noSearch = route.ready && route.entry.search === 'none'
  const word = CONJECTURE_LINE_WORD.priorArt
  const note = !route.ready
    ? `${route.keyLine} ${NO_DEMO}`
    : noSearch
      ? `${route.entry.name} has no web search, so this search would have nothing to come back empty from.`
      : PRIOR_ART_PRESS_NOTE
  return (
    <button
      type="button"
      className="action action--wide action--priced"
      style={{ '--act-tint': 'var(--sem-gap)' } as CSSProperties}
      // Greyed only where the line cannot take it, and still in the tab order:
      // its press path says why (D225).
      aria-disabled={shut || undefined}
      title={route.ready ? note : route.keyLine}
      aria-label={pressName(
        word,
        noSearch ? null : pressTail({ ready: route.ready, est: route.est }),
      )}
      aria-describedby={noteId}
      aria-keyshortcuts={keysLive ? 'V' : undefined}
      onClick={() => pressVerb('priorArt', node.id)}
    >
      <b style={{ width: '100%' }}>
        <Icon name="search" size={13} />
        {pressFace(word, route.ready && !noSearch, route.est)}
        <VerbPrice
          verb="priorArt"
          kbd={keysLive ? 'V' : undefined}
          instead={!route.ready ? STATUS_WORD.needsKey : noSearch ? null : undefined}
        />
      </b>
      <span id={noteId}>{note}</span>
    </button>
  )
}

/** What the executable press buys, said before it is pressed. */
export const EXECUTABLE_PRESS_NOTE =
  'Writes one computation that would tell the prediction apart from what it departs from, into Research Lab unrun — a model writes its evaluator, and nobody has reviewed it.'

/**
 * The executable-test press (D283). Paid, on the reason role, and it says before
 * it is pressed what it buys: a contract in Research Lab, unrun and unreviewed.
 * Running it is Research Lab's own press; a receipt, not this pass, reads it.
 *
 * It does not wait on the Up next line — no dispatcher but this press reaches its
 * pass — so under the lock it keeps its focus and says so by name (D251, D286).
 * Its one plain `disabled` is the structural refusal: a node whose registration,
 * wording or workspace no contract could be built from, whatever the key or lock.
 */
export function ExecutablePress({ session, node }: { session: Session; node: ReasonNode }) {
  const route = useLineRoute('executable')
  const busy = useStore((s) => s.busy)
  const noteId = useId()
  if (!node.conjecture) return null
  const refusal = executableRefusal(session, node)
  const word = 'Propose an executable test'
  const note = refusal ?? (route.ready ? EXECUTABLE_PRESS_NOTE : `${route.keyLine} ${NO_DEMO}`)
  const locked = busy && refusal === null
  return (
    <button
      type="button"
      className="action action--wide action--priced"
      style={{ '--act-tint': 'var(--sem-signal)' } as CSSProperties}
      disabled={refusal !== null}
      aria-disabled={locked || undefined}
      title={locked ? EXECUTABLE_LOCK_REFUSAL : route.ready ? note : route.keyLine}
      aria-label={pressName(
        word,
        refusal !== null ? null : pressTail({ ready: route.ready, est: route.est }),
      )}
      aria-describedby={noteId}
      onClick={() => void runExecutableTest(node.id)}
    >
      <b style={{ width: '100%' }}>
        <Icon name="experiment" size={13} />
        {pressFace(word, route.ready && refusal === null, route.est)}
        <VerbPrice
          verb="move"
          instead={refusal !== null ? null : route.ready ? undefined : STATUS_WORD.needsKey}
        />
      </b>
      <span id={noteId}>{note}</span>
    </button>
  )
}

/**
 * The consensus press: put what the conjecture departs from on the graph, free,
 * so the ordinary source check can ask whether specialists actually hold it.
 * Once landed, it goes to that node and says what its check read, if one ran —
 * which is a selection, and works under a pass. Landing it takes a step of
 * history, which a pass in flight would split, so that refuses under the lock by
 * name and keeps its focus (`DEPARTS_LOCK_REFUSAL`, D286).
 */
export function ConsensusPress({ session, node }: { session: Session; node: ReasonNode }) {
  const busy = useStore((s) => s.busy)
  const noteId = useId()
  if (!node.conjecture || node.parentId === null) return null
  const landed = consensusNodeOf(session, node.id)
  const reading = landed?.verification?.reading
  const locked = busy && !landed
  const word = landed ? 'Show what it departs from' : 'Put what it departs from on the graph'
  return (
    <button
      type="button"
      className="action action--wide"
      style={{ '--act-tint': 'var(--sem-signal)' } as CSSProperties}
      aria-disabled={locked || undefined}
      title={locked ? DEPARTS_LOCK_REFUSAL : undefined}
      // Named with what it spends, which is nothing (D165).
      aria-label={pressName(word, STATUS_WORD.free.toLowerCase())}
      aria-describedby={noteId}
      onClick={() => void landDepartsFrom(node.id)}
    >
      <b style={{ width: '100%' }}>
        <Icon name="plus" size={13} />
        {word}
      </b>
      <span id={noteId}>
        {landed
          ? reading
            ? `On the graph beside it. Its source check read: ${READING_LABEL[reading]}.`
            : 'On the graph beside it, not checked against sources yet. A check there asks whether specialists actually hold it.'
          : 'Lands what this insight departs from as a node beside it, at no charge, so a source check can ask whether specialists hold it.'}
      </span>
    </button>
  )
}

/**
 * The press. A wide row, because it is a paid pass that has to say what it buys
 * before it is pressed — the same shape as the source check's, and the same
 * press path: U's, which waits on the Up next line under a pass in flight (D286).
 */
export function ConjecturePress({ node }: { node: ReasonNode }) {
  const route = useLineRoute('conjecture')
  const shut = useStore((s) => upNextShut(s, 'conjecture'))
  // U acts on the selection (App's key handler), so its chip and its shortcut
  // are promised only on the selected node's card — the verbs' own rule
  // (`keysLive`), read here as the thread reads it.
  const keysLive = useStore((s) => s.selectedId === node.id)
  const noteId = useId()
  const word = CONJECTURE_LINE_WORD.conjecture
  return (
    <button
      type="button"
      className="action action--wide action--priced"
      style={{ '--act-tint': 'var(--sem-insight)' } as CSSProperties}
      aria-disabled={shut || undefined}
      title={
        route.ready
          ? 'Proposes claims no source the model knows states, each with a prediction that would tell it apart'
          : route.keyLine
      }
      aria-label={pressName(word, pressTail({ ready: route.ready, est: route.est }))}
      aria-describedby={noteId}
      aria-keyshortcuts={keysLive ? 'U' : undefined}
      onClick={() => pressVerb('conjecture', node.id)}
    >
      <b style={{ width: '100%' }}>
        <Icon name="conjecture" size={13} />
        {pressFace(word, route.ready, route.est)}
        <VerbPrice
          verb="move"
          kbd={keysLive ? 'U' : undefined}
          instead={route.ready ? undefined : STATUS_WORD.needsKey}
        />
      </b>
      <span id={noteId}>{route.ready ? CONJECTURE_PRESS_NOTE : `${route.keyLine} ${NO_DEMO}`}</span>
    </button>
  )
}
